import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { BsArrowLeft, BsDownload, BsEraser } from "react-icons/bs";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from "react-leaflet";
import Cabecalho from "../../../components/Cabecalho/Cabecalho";
import StatisticsNav from '../../../components/StatisticsNav';
import NatureMultiSelect from "../../../components/NatureMultiSelect";
import { rotuloNatureza } from "../../../Utils/naturezaLabel";
import api from "../../../Utils/api";
import { downloadCsv } from "../../../Utils/csvExport";
import { buildFilterSearch, readFiltersFromLocation } from "../../../Utils/statisticsFilterUrl";
import "../styles/dashboard-modern.css";
import "./estatisticas.css";
import "./mapa-ocorrencias.css";

const localToday = new Date();
const today = `${localToday.getFullYear()}-${String(localToday.getMonth() + 1).padStart(2, "0")}-${String(localToday.getDate()).padStart(2, "0")}`;
const startOfYear = `${localToday.getFullYear()}-01-01`;
const pageSize = 100;
const emptyOptions = { municipalities: [], neighborhoods: [], natures: [] };

function initialFilters(location) {
  return readFiltersFromLocation(location, {
    from: startOfYear,
    to: today,
    municipality: "",
    bairro: "",
    dateField: "fact",
    groupBy: "month",
    natureIds: [],
    natureId: "",
    compareWithPrevious: false,
  });
}

function HeatLayer({ cells }) {
  const map = useMap();
  const heatPoints = useMemo(
    () => cells.map(({ latitude, longitude, weight }) => [latitude, longitude, weight]),
    [cells]
  );

  useEffect(() => {
    if (!heatPoints.length) return undefined;
    const layer = L.heatLayer(heatPoints, {
      radius: 25,
      blur: 20,
      maxZoom: 16,
      minOpacity: 0.25,
    });
    layer.addTo(map);
    return () => map.removeLayer(layer);
  }, [heatPoints, map]);

  return null;
}

function FitMapToRecords({ cells }) {
  const map = useMap();
  useEffect(() => {
    if (!cells.length) return;
    const bounds = L.latLngBounds(cells.map(({ latitude, longitude }) => [latitude, longitude]));
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 14 });
  }, [cells, map]);
  return null;
}

function formatRegisteredDate(value) {
  if (!value) return "Não informada";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Não informada" : date.toLocaleString("pt-BR");
}

function MapaOcorrencias() {
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => initialFilters(location));
  const [draft, setDraft] = useState(() => initialFilters(location));
  const [options, setOptions] = useState(emptyOptions);
  const [mapData, setMapData] = useState(null);
  const [page, setPage] = useState(1);
  const [pointPage, setPointPage] = useState(1);
  const [view, setView] = useState("heat");
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [optionsError, setOptionsError] = useState("");
  const [validationError, setValidationError] = useState("");

  const natureOptions = useMemo(() => [
    ...(options.natures || []).map((nature) => ({
      value: String(nature.id),
      label: `${rotuloNatureza(nature)}${nature.ativo ? "" : " (inativa)"}`,
    })),
    { value: "pendente", label: "Pendentes de classificação" },
  ], [options.natures]);
  const selectedNatureOptions = useMemo(
    () => natureOptions.filter((option) => draft.natureIds.includes(option.value)),
    [natureOptions, draft.natureIds]
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
  }, []);

  useEffect(() => {
    let active = true;
    setOptionsLoading(true);
    setOptionsError("");
    api.get("/incidentReport/statistics/geographic/options", {
      params: { municipality: draft.municipality || undefined },
    })
      .then(({ data }) => {
        if (active) setOptions({ ...emptyOptions, ...data });
      })
      .catch((requestError) => {
        if (active) {
          setOptionsError(requestError.response?.data?.message || "Não foi possível carregar os municípios.");
        }
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [draft.municipality]);

  useEffect(() => {
    if (!filters.municipality) {
      setMapData(null);
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError("");
    setMapData(null);
    const natureIds = filters.natureIds || [];
    const queryFilters = { ...filters };
    delete queryFilters.natureIds;
    delete queryFilters.natureId;
    delete queryFilters.compareWithPrevious;
    const natureQuery = natureIds.length > 1
      ? { natureIds: natureIds.join(",") }
      : natureIds.length === 1
        ? { natureId: natureIds[0] }
        : {};
    api.get("/incidentReport/statistics/geographic", {
      params: { ...queryFilters, ...natureQuery, page, pointPage, pageSize },
    })
      .then(({ data }) => {
        if (active) setMapData(data);
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.response?.data?.message || "Não foi possível carregar os dados geográficos.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filters, page, pointPage]);

  const updateDraft = (field) => (event) => {
    const value = event.target.value;
    setDraft((current) => ({
      ...current,
      [field]: value,
      ...(field === "municipality" ? { bairro: "" } : {}),
    }));
  };

  const updateNatureSelection = (selectedOptions) => {
    const natureIds = [...new Set((selectedOptions || []).map((option) => String(option.value)))];
    setDraft((current) => ({
      ...current,
      natureIds,
      natureId: natureIds.length === 1 ? natureIds[0] : "",
    }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if (!draft.municipality) {
      setValidationError("Selecione um município para visualizar as ocorrências no mapa.");
      return;
    }
    if (!draft.from || !draft.to || draft.from > draft.to) {
      setValidationError("Informe um intervalo de datas válido.");
      return;
    }
    const elapsedDays = (Date.parse(`${draft.to}T00:00:00Z`) - Date.parse(`${draft.from}T00:00:00Z`)) /
      (24 * 60 * 60 * 1000);
    if (elapsedDays > 366) {
      setValidationError("O período da análise deve ser de no máximo 366 dias.");
      return;
    }
    setValidationError("");
    setPage(1);
    setPointPage(1);
    const nextFilters = { ...draft };
    setFilters(nextFilters);
    const search = buildFilterSearch(nextFilters);
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, {
      replace: true,
      state: { filters: nextFilters },
    });
  };

  const clearFilters = () => {
    const cleared = {
      from: startOfYear,
      to: today,
      municipality: "",
      natureIds: [],
      natureId: "",
      bairro: "",
      dateField: "fact",
      groupBy: "month",
    };
    setDraft(cleared);
    setFilters(cleared);
    setPage(1);
    setPointPage(1);
    setValidationError("");
    const search = buildFilterSearch(cleared);
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, {
      replace: true,
      state: { filters: cleared },
    });
  };

  const exportCurrentPage = () => {
    const records = mapData?.records?.items || [];
    downloadCsv(`bo-online-mapa-${filters.from}-${filters.to}-pagina-${page}.csv`,
      ["Boletim", "Data", "Natureza", "Bairro", "Município", "Coordenadas"],
      records.map((record) => [
        record.numero || record._id,
        filters.dateField === "fact" ? record.data : formatRegisteredDate(record.createdAt),
        record.natureza,
        record.localidadeNome || record.bairro,
        record.municipio,
        record.coordinateStatus === "valid" ? "Válidas" : record.coordinateStatus === "missing" ? "Ausentes" : "Inválidas",
      ]));
  };

  const heatCells = mapData?.heatmap?.cells || [];
  const points = mapData?.points?.items || [];
  const visibleLocations = view === "heat"
    ? heatCells
    : points.map((point) => ({ latitude: point.latitude, longitude: point.longitude }));
  const hasCoordinates = (mapData?.summary?.validCoordinates || 0) > 0;
  const mapOrigin = heatCells[0] || points[0];
  const mapCenter = mapOrigin
    ? [mapOrigin.latitude, mapOrigin.longitude]
    : undefined;

  return (
    <>
      <Cabecalho />
      <main className="statistics-page geographic-page">
        <Container>
          <StatisticsNav />
          <div className="statistics-heading">
            <div>
              <p className="dashboard-eyebrow">Análise geográfica</p>
              <h2>Mapa de Calor de Ocorrências</h2>
              <p className="dashboard-intro">
                Registros autorizados com coordenadas de ocorrência válidas. A localização não é estimada por endereço.
              </p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() => {
                const search = buildFilterSearch(filters);
                navigate({
                  pathname: "/dashboard/estatisticas",
                  search: search ? `?${search}` : "",
                }, { state: { filters } });
              }}
            >
              <BsArrowLeft className="me-2" /> Voltar às Estatísticas
            </Button>
          </div>

          <Form className="statistics-filters geographic-filters" onSubmit={applyFilters}>
            <Form.Group controlId="geographic-municipality">
              <Form.Label>Município</Form.Label>
              <Form.Select
                value={draft.municipality}
                onChange={updateDraft("municipality")}
                disabled={optionsLoading}
              >
                <option value="">Selecione um município</option>
                {options.municipalities.map((municipality) => (
                  <option key={municipality} value={municipality}>{municipality}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="geographic-from">
              <Form.Label>Data inicial</Form.Label>
              <Form.Control type="date" value={draft.from} max={draft.to} onChange={updateDraft("from")} />
            </Form.Group>
            <Form.Group controlId="geographic-to">
              <Form.Label>Data final</Form.Label>
              <Form.Control type="date" value={draft.to} min={draft.from} max={today} onChange={updateDraft("to")} />
            </Form.Group>
            <Form.Group controlId="geographic-nature">
              <Form.Label>Natureza</Form.Label>
              <NatureMultiSelect
                inputId="geographic-nature"
                ariaLabel="Natureza"
                value={selectedNatureOptions}
                options={natureOptions}
                isLoading={optionsLoading}
                isDisabled={optionsLoading || Boolean(optionsError)}
                onChange={updateNatureSelection}
                loadingMessage={() => "Carregando naturezas..."}
              />
            </Form.Group>
            <Form.Group controlId="geographic-neighborhood">
              <Form.Label>Bairro</Form.Label>
              <Form.Select
                value={draft.bairro}
                onChange={updateDraft("bairro")}
                disabled={!draft.municipality || optionsLoading}
              >
                <option value="">Todos os bairros</option>
                {options.neighborhoods.map((neighborhood) => (
                  <option key={neighborhood} value={neighborhood}>{neighborhood}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="geographic-date-field">
              <Form.Label>Data considerada</Form.Label>
              <Form.Select value={draft.dateField} onChange={updateDraft("dateField")}>
                <option value="fact">Data do fato</option>
                <option value="registered">Data de registro</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="geographic-group-by">
              <Form.Label>Agrupar por</Form.Label>
              <Form.Select value={draft.groupBy} onChange={updateDraft("groupBy")}>
                <option value="day">Dia</option>
                <option value="week">Semana</option>
                <option value="month">Mês</option>
              </Form.Select>
            </Form.Group>
            <div className="geographic-filter-actions">
              <Button type="submit">Aplicar filtros</Button>
              <Button type="button" variant="outline-secondary" onClick={clearFilters}>
                <BsEraser className="me-2" /> Limpar
              </Button>
            </div>
          </Form>

          {optionsError && <Alert variant="danger" className="mt-3">{optionsError}</Alert>}
          {validationError && <Alert variant="warning" className="mt-3" role="alert">{validationError}</Alert>}
          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {!draft.municipality && !optionsLoading && !optionsError && (
            <Alert variant="info" className="mt-3">
              Selecione um município e aplique os filtros para consultar ocorrências autorizadas.
              {options.municipalities.length === 0 && " Não há municípios disponíveis nos registros."}
            </Alert>
          )}
          {loading && (
            <div className="statistics-loading"><Spinner animation="border" role="status" /><span>Consultando ocorrências...</span></div>
          )}

          {mapData && !loading && (
            <>
              <Row className="g-3 statistics-indicators geographic-indicators">
                <Col xs={6} lg={4}><article className="stat-card"><span>Boletins no período</span><strong>{mapData.summary.totalReports}</strong></article></Col>
                <Col xs={6} lg={4}><article className="stat-card"><span>Com coordenadas válidas</span><strong>{mapData.summary.validCoordinates}</strong></article></Col>
                <Col xs={6} lg={4}><article className="stat-card"><span>Cobertura geográfica</span><strong>{mapData.summary.geoCoveragePercent}%</strong></article></Col>
                <Col xs={6} lg={4}><article className="stat-card"><span>Bairros com registros</span><strong>{mapData.neighborhoodCount}</strong></article></Col>
                <Col xs={6} lg={4}><article className="stat-card"><span>Natureza mais frequente</span><strong className="stat-card-detail">{mapData.natures[0]?.natureza || "—"}</strong></article></Col>
                <Col xs={6} lg={4}><article className="stat-card"><span>Bairro com mais registros</span><strong className="stat-card-detail">{mapData.neighborhoods[0]?.bairro || "—"}</strong></article></Col>
              </Row>
              <p className="text-muted small mt-2 mb-0">{mapData.natureCountingRule}</p>

              {(mapData.summary.missingCoordinates > 0 || mapData.summary.invalidCoordinates > 0) && (
                <Alert variant="warning" className="mt-3">
                  {mapData.summary.missingCoordinates} boletim(ns) sem coordenadas e {mapData.summary.invalidCoordinates} com coordenadas incompletas ou inválidas não aparecem no mapa, mas continuam incluídos nas estatísticas gerais. O sistema não inventa, inverte ou corrige coordenadas.
                </Alert>
              )}
              {mapData.heatmap.truncated && (
                <Alert variant="info" className="mt-3">
                  O heatmap contém os 5.000 agrupamentos espaciais mais concentrados; os indicadores continuam contando todos os boletins filtrados.
                </Alert>
              )}
              {mapData.summary.totalReports === 0 && (
                <Alert variant="info" className="mt-3">Nenhum boletim foi encontrado para os filtros selecionados.</Alert>
              )}
              {mapData.summary.totalReports > 0 && !hasCoordinates && (
                <Alert variant="warning" className="mt-3">
                  Não há ocorrências com coordenadas válidas neste período. O mapa não será centralizado artificialmente; consulte o ranking e os dados tabulares abaixo.
                </Alert>
              )}

              {hasCoordinates && mapOrigin && (
                <section className="chart-card geographic-map-card">
                  <div className="geographic-map-heading">
                    <div>
                      <h3>{filters.municipality}</h3>
                      <span>Localizações reais dos boletins; não há limites geográficos confiáveis para validar pontos invertidos.</span>
                    </div>
                    <Form.Select
                      aria-label="Camada do mapa"
                      value={view}
                      onChange={(event) => setView(event.target.value)}
                    >
                      <option value="heat">Mapa de calor</option>
                      <option value="points">Pontos individuais (página atual)</option>
                    </Form.Select>
                  </div>
                  {view === "heat" && (
                    <div className="heat-legend" aria-label="Legenda do mapa de calor">
                      <span>Menor concentração</span><i /><span>Maior concentração</span>
                    </div>
                  )}
                  <MapContainer
                    key={filters.municipality}
                    className="geographic-map"
                    center={mapCenter}
                    zoom={5}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitMapToRecords cells={visibleLocations} />
                    {view === "heat" && <HeatLayer cells={heatCells} />}
                    {view === "points" && points.map((point) => (
                      <CircleMarker
                        key={point._id}
                        center={[point.latitude, point.longitude]}
                        radius={7}
                        pathOptions={{ color: "#123f68", fillColor: "#2585d8", fillOpacity: 0.8 }}
                      >
                        <Popup>
                          <dl className="map-popup-details">
                            <dt>Data do {filters.dateField === "fact" ? "fato" : "registro"}</dt>
                            <dd>{filters.dateField === "fact" ? point.data : formatRegisteredDate(point.createdAt)}</dd>
                            <dt>Natureza</dt><dd>{point.natureza || "Não informada"}</dd>
                            <dt>Localidade</dt><dd>{point.localidadeNome || point.bairro || "Não informado"}</dd>
                            <dt>Município</dt><dd>{point.municipio}</dd>
                            <dt>Boletim</dt><dd>{point.numero || point._id}</dd>
                          </dl>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                  <p className="geographic-map-note">
                    A camada de calor agrupa coordenadas em células aproximadas de 100 m; a intensidade corresponde à quantidade de boletins.
                  </p>
                  {view === "points" && (
                    <div className="geographic-pagination geographic-point-pagination">
                      <span>{mapData.points.total} pontos válidos · página {mapData.points.page} de {Math.max(mapData.points.pages, 1)}</span>
                      <div>
                        <Button
                          variant="outline-secondary"
                          disabled={pointPage <= 1 || loading}
                          onClick={() => setPointPage((current) => current - 1)}
                        >
                          Pontos anteriores
                        </Button>
                        <Button
                          variant="outline-secondary"
                          disabled={pointPage >= mapData.points.pages || loading}
                          onClick={() => setPointPage((current) => current + 1)}
                        >
                          Próximos pontos
                        </Button>
                      </div>
                    </div>
                  )}
                </section>
              )}
              {hasCoordinates && !mapOrigin && (
                <Alert variant="warning" className="mt-3">
                  Há boletins contabilizados com coordenadas válidas, mas nenhum ponto foi disponibilizado para desenhar o mapa.
                </Alert>
              )}

              <Row className="g-3 mt-1">
                <Col xs={12} xl={7}>
                  <section className="chart-card geographic-timeline-card">
                    <h3>Boletins por {filters.groupBy === "day" ? "dia" : filters.groupBy === "week" ? "semana" : "mês"}</h3>
                    <p className="text-muted small">Os valores representam registros no sistema, não a ausência ou ocorrência real de crimes.</p>
                    {mapData.timeline.length ? (
                      <div className="geographic-timeline">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={mapData.timeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                            <XAxis dataKey="periodo" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="total" name="Boletins" fill="#2585d8" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <p className="mb-0">Sem registros no período.</p>}
                  </section>
                </Col>
                <Col xs={12} xl={5}>
                  <section className="statistics-table-card geographic-ranking">
                    <h3>Ocorrências por bairro</h3>
                    <Table responsive className="statistics-table mb-0">
                      <thead><tr><th>Bairro</th><th>Boletins</th></tr></thead>
                      <tbody>
                        {mapData.neighborhoods.map((item) => (
                          <tr key={item.bairro}><td>{item.bairro}</td><td>{item.total}</td></tr>
                        ))}
                        {!mapData.neighborhoods.length && <tr><td colSpan="2">Sem bairros informados.</td></tr>}
                      </tbody>
                    </Table>
                  </section>
                </Col>
              </Row>

              <section className="statistics-table-card geographic-records">
                <div className="geographic-records-heading">
                  <h3>Boletins do período</h3>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={exportCurrentPage}
                    disabled={!mapData.records?.items?.length}
                  >
                    <BsDownload className="me-2" /> Exportar página CSV
                  </Button>
                </div>
                <p className="text-muted small">
                  Inclui registros sem localização. Somente número, data, natureza, bairro e município;
                  dados pessoais e histórico não são retornados.
                </p>
                <Table responsive className="statistics-table mb-0">
                  <thead>
                    <tr><th>Boletim</th><th>Data</th><th>Natureza</th><th>Bairro</th><th>Município</th><th>Coordenadas</th></tr>
                  </thead>
                  <tbody>
                    {(mapData.records?.items || []).map((point) => (
                      <tr key={point._id}>
                        <td>{point.numero || point._id}</td>
                        <td>{filters.dateField === "fact" ? point.data : formatRegisteredDate(point.createdAt)}</td>
                        <td>{point.natureza || "Não informada"}</td>
                        <td>{point.localidadeNome || point.bairro || "Não informado"}</td>
                        <td>{point.municipio}</td>
                        <td>{point.coordinateStatus === "valid" ? "Válidas" : point.coordinateStatus === "missing" ? "Ausentes" : "Inválidas"}</td>
                      </tr>
                    ))}
                    {!mapData.records?.items?.length && <tr><td colSpan="6">Nenhum boletim nesta página.</td></tr>}
                  </tbody>
                </Table>
                <div className="geographic-pagination">
                  <span>
                    {mapData.records?.total || 0} boletins · página {mapData.records?.page || 1} de {Math.max(mapData.records?.pages || 0, 1)}
                  </span>
                  <div>
                    <Button
                      variant="outline-secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline-secondary"
                      disabled={page >= (mapData.records?.pages || 0) || loading}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              </section>
            </>
          )}
        </Container>
      </main>
    </>
  );
}

export default MapaOcorrencias;
